import type { ReferralCodes, ReferralUrlParams } from "../types"

/**
 * Retrieves the value of a specified URL parameter from the given UrlParams object.
 *
 * @param urlParams - An object containing URL parameters.
 * @param key - The key of the URL parameter to retrieve. Must be one of:
 *   "coupon", "couponCode", "ref", "referralCode", "rwg_token", "utm_campaign", or "utm_medium".
 * @returns The value of the specified URL parameter as a string, or `undefined` if the parameter is not present.
 */
const getUrlParam = (
  urlParams: ReferralUrlParams,
  key: "coupon" | "couponCode" | "ref" | "referralCode" | "rwg_token" | "utm_campaign" | "utm_medium",
): string | undefined => {
  const param = urlParams[key]
  if (param) {
    return Array.isArray(param) ? param[0] : param
  }

  return undefined
}

/**
 * Extracts codes from the given URL parameters.
 * - Coupon: Retrieved from `coupon` or `couponCode` URL params.
 * - Referral: Retrieved from `ref` or `referralCode` URL params.
 * - RWG Token: Retrieved from `rwg_token` URL param.
 */
export const getCodesFromParams = (urlParams: ReferralUrlParams): ReferralCodes => {
  return {
    coupon: getUrlParam(urlParams, "coupon") ?? getUrlParam(urlParams, "couponCode"),
    referral: getUrlParam(urlParams, "ref") ?? getUrlParam(urlParams, "referralCode"),
    rwgToken: getUrlParam(urlParams, "rwg_token"),
  }
}

/**
 * Prioritize walk-ins referrals over persisted ones.
 * Prioritize persisted ones over referrals from params (when not a walk-in use-case)
 */
const getPrioritizedReferralCode = (
  urlCodes: ReferralCodes,
  persistedCodes: ReferralCodes,
  urlParams: ReferralUrlParams,
): string | undefined => {
  // Bypasses referral code if coming from QR code for partner acquired bookings
  const isPartnerAcquired = urlParams.utm_campaign === "hotels"
  if (isPartnerAcquired) {
    return undefined
  }
  // https://www.notion.so/usebounce/UTM-Parameters-8e3c98a432f94497adb8757648799b8d
  const isWalkIn =
    urlParams.utm_campaign === "signage" ||
    urlParams.utm_campaign === "partner_qr_code_booking" ||
    urlParams.utm_campaign === "walk_in" ||
    (urlParams.utm_campaign === "affiliate" && urlParams.utm_medium === "qrcode")

  // Override persisted referral code if it's a walk-in and use the code from the url params
  if (isWalkIn) {
    return urlCodes.referral
  }

  return persistedCodes.referral ?? urlCodes.referral
}

export const mergeCodes = (
  urlCodes: ReferralCodes,
  persistedCodes: ReferralCodes,
  urlParams: ReferralUrlParams,
): ReferralCodes => {
  return {
    referral: getPrioritizedReferralCode(urlCodes, persistedCodes, urlParams),
    coupon: urlCodes.coupon ?? persistedCodes.coupon,
    rwgToken: persistedCodes.rwgToken ?? urlCodes.rwgToken, // Rwg token should be sticky on 1st visit
  }
}
